/**
 * Path-keyed object helpers shared by the coordinator and useEditor.
 *
 * "Path" means a dotted-string key: `"publicContext.cpvClassification.mainCode"`.
 * Segments may be plain object keys or canonical non-negative integers
 * (`"0"`, `"12"`) which index into arrays — e.g.
 * `"awardCriteria.criteria.0.weight.number"`. Leaves can be any value —
 * primitive, array, or null.
 *
 * Array semantics — arrays are atomic merge units (RFC 7396):
 *   - `hasPath` walks through arrays to answer membership questions
 *   - `removePath` treats the array as the unit of removal: descending past it
 *     drops the *whole* array from the result (a stripped-down element would
 *     wholesale-replace the server's array and lose the other element fields)
 *   - `pickPaths` mirrors that: an array-deep path snapshots the whole array
 *     at the closest plain-object ancestor — so `removePaths ⊕ pickPaths = id`
 *     for the same paths, given `applyPatchToState`'s wholesale-replace
 *
 * All dynamic-key access is routed through `hasKey` / `readKey` to (a)
 * dispatch array vs plain-object access in one place and (b) reject the
 * prototype-polluting keys `__proto__`, `prototype`, and `constructor`
 * (see CWE-915).
 */

const ARRAY_INDEX = /^(0|[1-9]\d*)$/;
const DANGEROUS_KEYS = new Set(["__proto__", "prototype", "constructor"]);

type Container = Record<string, unknown> | unknown[];

function isArrayIndex(segment: string): boolean {
  return ARRAY_INDEX.test(segment);
}

function isSafeObjectKey(key: string): boolean {
  return !DANGEROUS_KEYS.has(key);
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isContainer(value: unknown): value is Container {
  return Array.isArray(value) || isPlainObject(value);
}

function hasKey(container: Container, key: string): boolean {
  if (Array.isArray(container)) {
    return isArrayIndex(key) && Number(key) < container.length;
  }
  if (!isSafeObjectKey(key)) return false;
  return Object.prototype.hasOwnProperty.call(container, key);
}

function readKey(container: Container, key: string): unknown {
  if (Array.isArray(container)) {
    if (!isArrayIndex(key)) return undefined;
    const idx = Number(key);
    return idx < container.length ? container[idx] : undefined;
  }
  if (!isSafeObjectKey(key)) return undefined;
  if (!Object.prototype.hasOwnProperty.call(container, key)) return undefined;
  return container[key];
}

function deleteObjectKey(obj: Record<string, unknown>, key: string): void {
  if (!isSafeObjectKey(key)) return;
  delete obj[key];
}

export function leafPaths(fields: Record<string, unknown>, prefix = ""): string[] {
  return Object.entries(fields).flatMap(([key, value]) =>
    isPlainObject(value) && Object.keys(value).length > 0 ? leafPaths(value, `${prefix}${key}.`) : [`${prefix}${key}`],
  );
}

export function hasPath(obj: Partial<Record<string, unknown>>, path: string): boolean {
  let current: unknown = obj;
  for (const part of path.split(".")) {
    if (!isContainer(current) || !hasKey(current, part)) return false;
    current = readKey(current, part);
  }
  return true;
}

/**
 * Reads the value at a dotted path, walking through arrays the same way
 * `hasPath` does. Returns `undefined` for any unreachable segment (missing
 * key, out-of-bounds index, dangerous key, non-container intermediate).
 * Cannot distinguish "value is undefined" from "path missing" — callers
 * relying on that distinction should use `hasPath` first.
 */
export function readPath(obj: Partial<Record<string, unknown>>, path: string): unknown {
  let current: unknown = obj;
  for (const part of path.split(".")) {
    if (!isContainer(current) || !hasKey(current, part)) return undefined;
    current = readKey(current, part);
  }
  return current;
}

/**
 * Returns a new object with the given dotted paths removed. Branches that
 * become empty plain objects are pruned. If a path descends through an array,
 * the whole array is dropped (arrays are atomic per RFC 7396 merge semantics).
 * The input is not mutated.
 */
export function removePaths(
  fields: Record<string, unknown>,
  paths: string[],
): Record<string, unknown> {
  return paths.reduce(removePath, fields);
}

export function removePath(
  fields: Record<string, unknown>,
  path: string,
): Record<string, unknown> {
  const result = removePathParts(fields, path.split("."));
  return (result as Record<string, unknown> | undefined) ?? {};
}

function removePathParts(
  fields: Record<string, unknown>,
  parts: string[],
): Record<string, unknown> | undefined {
  const [key, ...rest] = parts;
  if (key === undefined || !isSafeObjectKey(key) || !Object.prototype.hasOwnProperty.call(fields, key)) {
    return fields;
  }

  const next = { ...fields };
  if (rest.length === 0) {
    deleteObjectKey(next, key);
    return Object.keys(next).length > 0 ? next : undefined;
  }

  const value = readKey(next, key);
  if (!isPlainObject(value)) {
    deleteObjectKey(next, key);
    return Object.keys(next).length > 0 ? next : undefined;
  }

  const child = removePathParts(value, rest);
  if (child === undefined) {
    deleteObjectKey(next, key);
  } else {
    next[key] = child;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Returns a new object containing only the given dotted paths from the input.
 * For paths that descend into an array, the whole array is snapshotted at the
 * closest plain-object ancestor — preserving the round-trip identity
 *   removePaths(obj, P) ⊕ pickPaths(obj, P) = obj
 * given that `applyPatchToState` replaces arrays wholesale. Paths not
 * reachable in the input are silently skipped. The input is not mutated.
 */
export function pickPaths(
  fields: Record<string, unknown>,
  paths: string[],
): Record<string, unknown> {
  let result: Record<string, unknown> = {};
  for (const path of paths) {
    const reached = readReachablePath(fields, path);
    if (reached === null) continue;
    result = setPath(result, reached.path, reached.value);
  }
  return result;
}

function readReachablePath(
  fields: Record<string, unknown>,
  path: string,
): { path: string; value: unknown } | null {
  const parts = path.split(".");
  let current: unknown = fields;
  const reached: string[] = [];
  for (const part of parts) {
    if (!isPlainObject(current) || !hasKey(current, part)) break;
    reached.push(part);
    current = readKey(current, part);
  }
  return reached.length > 0 ? { path: reached.join("."), value: current } : null;
}

function setPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const parts = path.split(".");
  if (parts.some((p) => !isSafeObjectKey(p))) return obj;

  const next = { ...obj };
  let cursor: Record<string, unknown> = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const existing = readKey(cursor, key);
    const child = isPlainObject(existing) ? { ...existing } : {};
    cursor[key] = child;
    cursor = child;
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}
